import { isRecord, isString } from 'utils/typeGuards';

import { parseIsoDate } from './productUpdateDates';

const ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const MAX_ID_LENGTH = 80;
const MAX_TITLE_LENGTH = 120;
const MAX_SUMMARY_LENGTH = 280;
const MAX_TAG_LENGTH = 40;
const MAX_TAGS = 6;
const MAX_DETAILS_LENGTH = 4000;
const MAX_IMAGES = 6;
const MAX_IMAGE_TEXT_LENGTH = 200;
const IMAGE_SRC_PATTERN =
  /^\/images\/[a-z0-9][a-z0-9./_-]*\.(?:png|jpe?g|webp)$/;

export type ProductUpdateImage = {
  /** Root-relative docs image path, such as `/images/product-updates/example.png`. */
  src: string;
  alt: string;
  caption: string | null;
};

export type ProductUpdate = {
  id: string;
  /** UTC calendar date the update ships (`YYYY-MM-DD`). */
  publishedOn: string;
  title: string;
  /** One or two sentences for the popup and the changelog lede. */
  summary: string;
  /** Extra changelog prose. Plain text; blank lines break paragraphs. */
  details: string | null;
  /** Screenshots on Product Updates only. The card does not show these. */
  images: readonly ProductUpdateImage[];
  tags: readonly string[];
  /** When false, the entry is changelog-only and never opens the popup. */
  showInPopup: boolean;
};

export class ProductUpdateFileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProductUpdateFileError';
  }
}

function fail(index: number, message: string): never {
  throw new ProductUpdateFileError(`updates[${index}]: ${message}`);
}

function requireLine(index: number, field: string, value: unknown): string {
  if (!isString(value)) fail(index, `${field} must be a string`);
  const trimmed = value.trim();
  if (!trimmed) fail(index, `${field} is empty`);
  if (trimmed !== value) fail(index, `${field} has surrounding whitespace`);
  if (/[\r\n]/.test(trimmed)) fail(index, `${field} must be a single line`);
  return trimmed;
}

function parseTags(index: number, value: unknown): string[] {
  if (!Array.isArray(value)) fail(index, 'tags must be an array');
  if (value.length > MAX_TAGS) {
    fail(index, `tags has more than ${MAX_TAGS} items`);
  }
  const tags: string[] = [];
  for (const tag of value) {
    if (!isString(tag)) fail(index, 'tags must be strings');
    const trimmed = tag.trim();
    if (!trimmed || trimmed !== tag || /[\r\n]/.test(tag)) {
      fail(index, 'each tag must be a single non-empty line');
    }
    if (trimmed.length > MAX_TAG_LENGTH) {
      fail(index, `tag is longer than ${MAX_TAG_LENGTH} characters`);
    }
    if (tags.includes(trimmed)) fail(index, `duplicate tag "${trimmed}"`);
    tags.push(trimmed);
  }
  return tags;
}

function parseImages(index: number, value: unknown): ProductUpdateImage[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) fail(index, 'images must be an array');
  if (value.length > MAX_IMAGES) {
    fail(index, `images has more than ${MAX_IMAGES} items`);
  }
  return value.map((image, imageIndex) => {
    if (!isRecord(image)) {
      fail(index, `images[${imageIndex}] must be an object`);
    }
    const src = requireLine(index, `images[${imageIndex}].src`, image.src);
    if (!IMAGE_SRC_PATTERN.test(src)) {
      fail(
        index,
        `images[${imageIndex}].src must be a docs image path under /images/`
      );
    }
    const alt = requireLine(index, `images[${imageIndex}].alt`, image.alt);
    if (alt.length > MAX_IMAGE_TEXT_LENGTH) {
      fail(index, `images[${imageIndex}].alt is too long`);
    }
    const captionValue = image.caption;
    let caption: string | null = null;
    if (captionValue !== undefined && captionValue !== null) {
      caption = requireLine(
        index,
        `images[${imageIndex}].caption`,
        captionValue
      );
      if (caption.length > MAX_IMAGE_TEXT_LENGTH) {
        fail(index, `images[${imageIndex}].caption is too long`);
      }
    }
    return { src, alt, caption };
  });
}

function parseDetails(index: number, value: unknown): string | null {
  if (value === undefined || value === null) return null;
  if (!isString(value)) fail(index, 'details must be a string');
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.length > MAX_DETAILS_LENGTH) {
    fail(index, `details is longer than ${MAX_DETAILS_LENGTH} characters`);
  }
  return trimmed;
}

function parseEntry(value: unknown, index: number): ProductUpdate {
  if (!isRecord(value)) fail(index, 'expected an object');
  const id = requireLine(index, 'id', value.id);
  if (!ID_PATTERN.test(id) || id.length > MAX_ID_LENGTH) {
    fail(
      index,
      'id must be a short hyphenated slug (lowercase letters, numbers)'
    );
  }
  const publishedOn = requireLine(index, 'publishedOn', value.publishedOn);
  if (!parseIsoDate(publishedOn)) {
    fail(index, 'publishedOn must be a real YYYY-MM-DD date');
  }
  const title = requireLine(index, 'title', value.title);
  if (title.length > MAX_TITLE_LENGTH) {
    fail(index, `title is longer than ${MAX_TITLE_LENGTH} characters`);
  }
  const summary = requireLine(index, 'summary', value.summary);
  if (summary.length > MAX_SUMMARY_LENGTH) {
    fail(index, `summary is longer than ${MAX_SUMMARY_LENGTH} characters`);
  }
  if (typeof value.showInPopup !== 'boolean') {
    fail(index, 'showInPopup must be true or false');
  }
  return {
    id,
    publishedOn,
    title,
    summary,
    details: parseDetails(index, value.details),
    images: parseImages(index, value.images),
    tags: parseTags(index, value.tags),
    showInPopup: value.showInPopup,
  };
}

/** Drop `**bold**` markers. The docs page renders them; plain-text surfaces do not. */
export function plainProductUpdateText(value: string): string {
  return value.replace(/\*\*([^*\n]+)\*\*/g, '$1');
}

/** Newest `publishedOn` first. Same-day entries break ties by id descending. */
export function compareProductUpdatesNewestFirst(
  left: ProductUpdate,
  right: ProductUpdate
): number {
  if (left.publishedOn !== right.publishedOn) {
    return left.publishedOn < right.publishedOn ? 1 : -1;
  }
  if (left.id === right.id) return 0;
  return left.id < right.id ? 1 : -1;
}

export function parseProductUpdates(raw: unknown): ProductUpdate[] {
  if (!Array.isArray(raw)) {
    throw new ProductUpdateFileError('updates file must be a JSON array');
  }
  const updates = raw.map((entry, index) => parseEntry(entry, index));
  const seen = new Set<string>();
  for (const update of updates) {
    if (seen.has(update.id)) {
      throw new ProductUpdateFileError(`duplicate update id "${update.id}"`);
    }
    seen.add(update.id);
  }
  return updates;
}
